import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { useEffect, useRef } from 'react'
import {
    BufferAttribute,
    BufferGeometry,
    Color,
    NormalBlending,
    Points,
    PointsMaterial,
    Vector3,
} from 'three'
import { useFootstepEffectTextures } from '../../effects/useFootstepEffectTextures'
import { SHIELD_BREAK_EVENT } from './shieldBreakEvent'
import {
    shieldBreakEffectCenterY,
    shieldBreakEffectColor,
    shieldBreakEffectCount,
    shieldBreakEffectDrag,
    shieldBreakEffectEndSize,
    shieldBreakEffectHalfExtentsX,
    shieldBreakEffectHalfExtentsY,
    shieldBreakEffectHalfExtentsZ,
    shieldBreakEffectLifetime,
    shieldBreakEffectOpacity,
    shieldBreakEffectOutwardVelocity,
    shieldBreakEffectRandomVelocity,
    shieldBreakEffectSize,
    shieldBreakEffectSpeed,
    shieldBreakEffectUpwardDrift,
} from '../../config/constants'

type ActiveBurst = {
    age: number
    geometry: BufferGeometry
    material: PointsMaterial
    points: Points
    positions: Float32Array
    velocities: Float32Array
}

const _localPosition = new Vector3()
const _velocity = new Vector3()
const _halfExtents = new Vector3()

export function ShieldBreakEffect() {
    const rootRef = useRef<THREE.Group | null>(null)
    const burstsRef = useRef<ActiveBurst[]>([])
    const queueRef = useRef(0)
    const { dustTexture } = useFootstepEffectTextures()

    useEffect(() => {
        const handleBreak = () => {
            queueRef.current += 1
        }
        window.addEventListener(SHIELD_BREAK_EVENT, handleBreak)
        return () => {
            window.removeEventListener(SHIELD_BREAK_EVENT, handleBreak)
            for (const burst of burstsRef.current) {
                burst.points.removeFromParent()
                burst.geometry.dispose()
                burst.material.dispose()
            }
            burstsRef.current = []
        }
    }, [])

    function spawnBurst() {
        const count = Math.max(1, Math.floor(shieldBreakEffectCount))
        const positions = new Float32Array(count * 3)
        const velocities = new Float32Array(count * 3)
        const geometry = new BufferGeometry()
        geometry.setAttribute('position', new BufferAttribute(positions, 3))

        for (let index = 0; index < count; index += 1) {
            const i3 = index * 3

            _localPosition.set(
                (Math.random() * 2 - 1) * _halfExtents.set(
                    shieldBreakEffectHalfExtentsX,
                    shieldBreakEffectHalfExtentsY,
                    shieldBreakEffectHalfExtentsZ,
                ).x,
                (Math.random() * 2 - 1) * _halfExtents.y,
                (Math.random() * 2 - 1) * _halfExtents.z,
            )

            positions[i3] = _localPosition.x
            positions[i3 + 1] = _localPosition.y
            positions[i3 + 2] = _localPosition.z

            _velocity.copy(_localPosition).normalize().multiplyScalar(
                shieldBreakEffectOutwardVelocity * (0.65 + Math.random() * 0.7),
            )
            _velocity.x += (Math.random() * 2 - 1) * shieldBreakEffectRandomVelocity
            _velocity.y += shieldBreakEffectUpwardDrift + Math.random() * 0.2
            _velocity.z += (Math.random() * 2 - 1) * shieldBreakEffectRandomVelocity

            velocities[i3] = _velocity.x
            velocities[i3 + 1] = _velocity.y
            velocities[i3 + 2] = _velocity.z
        }

        const material = new PointsMaterial({
            color: new Color(shieldBreakEffectColor),
            size: shieldBreakEffectSize,
            alphaMap: dustTexture,
            transparent: true,
            opacity: shieldBreakEffectOpacity,
            depthWrite: false,
            blending: NormalBlending,
            sizeAttenuation: true,
            toneMapped: false,
        })

        const points = new Points(geometry, material)
        points.position.set(0, shieldBreakEffectCenterY, 0)
        rootRef.current?.add(points)

        burstsRef.current.push({
            age: 0,
            geometry,
            material,
            points,
            positions,
            velocities,
        })
    }

    useFrame((_, delta) => {
        const safeDelta = Math.min(delta, 1 / 30)

        while (queueRef.current > 0) {
            queueRef.current -= 1
            spawnBurst()
        }

        for (let index = burstsRef.current.length - 1; index >= 0; index -= 1) {
            const burst = burstsRef.current[index]
            burst.age += safeDelta
            const t = burst.age / Math.max(shieldBreakEffectLifetime, 0.0001)

            if (t >= 1) {
                burst.points.removeFromParent()
                burst.geometry.dispose()
                burst.material.dispose()
                burstsRef.current.splice(index, 1)
                continue
            }

            const drag = Math.exp(-shieldBreakEffectDrag * safeDelta)

            for (let p = 0; p < burst.positions.length; p += 3) {
                burst.positions[p] += burst.velocities[p] * shieldBreakEffectSpeed * safeDelta
                burst.positions[p + 1] += burst.velocities[p + 1] * shieldBreakEffectSpeed * safeDelta
                burst.positions[p + 2] += burst.velocities[p + 2] * shieldBreakEffectSpeed * safeDelta

                burst.velocities[p] *= drag
                burst.velocities[p + 1] *= drag
                burst.velocities[p + 2] *= drag
            }

            burst.geometry.attributes.position.needsUpdate = true
            burst.material.color.set(shieldBreakEffectColor)
            burst.material.opacity = shieldBreakEffectOpacity * Math.pow(1 - t, 1.6)
            burst.material.size = shieldBreakEffectSize + (shieldBreakEffectEndSize - shieldBreakEffectSize) * t
        }
    })

    return <group ref={rootRef} />
}
